import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { uploadReceivePhoto } from '../lib/photoUpload.js'
import { resolveActiveRestaurantId } from '../lib/activeRestaurant.js'
import { matchStaffName, normalizeStaffName, assignmentDedupeKey } from '../lib/rosterMatch.js'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'
import KitchenLoader from './KitchenLoader.jsx'

// "InvoiceScanModal for rosters." Clone of the proven Ship A invoice
// scan pipeline, adapted to extract staff + shifts from a photo of a
// printed schedule or staff list.
//
// Flow:
//   1. PICK      — manager snaps/picks a photo. Unit resolved ONCE here.
//   2. UPLOAD    — photo → private receive-photos bucket (path only).
//   3. EXTRACT   — extract-roster edge fn (Claude Vision) → { staff, shifts }.
//   4. REVIEW    — editable preview: staff rows (create/match/skip) +
//                  shift rows (matched-staff badge, approve toggle).
//   5. CONFIRM   — two-step client-side RLS write with EXPLICIT
//                  restaurant_id on every row:
//                    A. insert NEW staff (dedupe vs existing)
//                    B. map shifts → staff_id (existing ∪ just-created)
//                    C. insert schedule_assignments (dedupe by key)
//
// Images only (v1): non-image / PDF / 0-byte / >12MB are rejected with a
// deterministic nudge BEFORE any upload or model call.
export default function RosterScanModal({
  open,
  onClose,
  onImported,        // ({ staffCreated, shiftsInserted, skippedDupe, skippedUnmatched, skippedNoEnd }) → void
  activeStaff,       // [{ id, name, role, ... }, ...] existing staff for matching
  kind = 'both',     // 'schedule' | 'staff' | 'both'
}) {
  const fileRef = useRef(null)
  const { currentRestaurantId, isGrouped, loading } = useCurrentUnit()
  const [phase, setPhase] = useState('pick')
  // 'pick' | 'uploading' | 'extracting' | 'review' | 'submitting' | 'error' | 'fallback'
  const [filename, setFilename] = useState('')
  const [photoPreview, setPhotoPreview] = useState(null)  // local object URL
  const [photoPath, setPhotoPath] = useState(null)        // private Storage object path
  // Destination unit resolved ONCE at photo-pick time. Reused at submit
  // so the photo, the new staff, and the assignments all land on the SAME
  // unit the operator had selected when the photo was taken.
  const [resolvedRestaurantId, setResolvedRestaurantId] = useState(null)
  const [error, setError] = useState(null)
  const [fallbackMsg, setFallbackMsg] = useState('')

  // Editable preview state.
  const [staffRows, setStaffRows] = useState([])
  // each: { id, name, role, action: 'create' | 'match' | 'skip', matchedId, matchedName }
  const [shiftRows, setShiftRows] = useState([])
  // each: { id, day, shift_date, start_time, end_time, role, staff_name,
  //         matchedId, matchedName, approved }

  // Reset on every open so a new scan starts clean.
  useEffect(() => {
    if (open) {
      setPhase('pick')
      setFilename('')
      setPhotoPath(null)
      setResolvedRestaurantId(null)
      setError(null)
      setFallbackMsg('')
      setStaffRows([])
      setShiftRows([])
      if (photoPreview) URL.revokeObjectURL(photoPreview)
      setPhotoPreview(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup local object URL on unmount.
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  // Counts for the footer CTA / review summary.
  const staffToCreate = useMemo(
    () => staffRows.filter((s) => s.action === 'create').length,
    [staffRows]
  )
  // A shift is WRITABLE only with a matched staff AND a start AND an end
  // AND a date. schedule_assignments.end_time is NOT NULL — a shift with
  // a start but no end can NEVER be inserted, so it is not "approvable".
  const shiftsApproved = useMemo(
    () =>
      shiftRows.filter(
        (s) =>
          s.approved &&
          s.matchedId !== null &&
          !!s.shift_date &&
          !!s.start_time &&
          !!s.end_time
      ).length,
    [shiftRows]
  )
  const shiftsUnmatched = useMemo(
    () => shiftRows.filter((s) => s.matchedId === null).length,
    [shiftRows]
  )
  // Matched + approved but blocked from import because they're missing an
  // end time. Surfaced as a distinct "add an end time" nudge so the row
  // isn't silently dropped (and we never fabricate a default end time).
  const shiftsMissingEnd = useMemo(
    () =>
      shiftRows.filter(
        (s) => s.approved && s.matchedId !== null && !!s.start_time && !s.end_time
      ).length,
    [shiftRows]
  )

  // ---------- ALL HOOKS ABOVE THIS LINE (React #310) -------
  if (!open) return null

  // Re-match a shift's staff_name against existing ∪ just-to-be-created
  // staff. "matchedId" is the existing staff id; for a brand-new staff
  // (create action) it is the negative sentinel index so we can wire it
  // to the inserted id at submit. Returns { matchedId, matchedName }.
  function resolveShiftMatch(staffName, currentStaffRows) {
    const existing = matchStaffName(staffName, activeStaff)
    if (existing) {
      return { matchedId: existing.staff.id, matchedName: existing.staff.name }
    }
    // Will-be-created staff: match by normalized name against the
    // create-action staff rows. Use the row id as a pending marker.
    const norm = normalizeStaffName(staffName)
    const pending = (currentStaffRows ?? staffRows).find(
      (s) => s.action === 'create' && normalizeStaffName(s.name) === norm
    )
    if (pending) {
      return { matchedId: `pending:${pending.id}`, matchedName: pending.name }
    }
    return { matchedId: null, matchedName: null }
  }

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return

    // Pre-call guard (deterministic, BEFORE upload/model).
    // HEIC/HEIF are common iPhone formats but Anthropic Vision does NOT
    // accept them — reject up front with a clear, deterministic message
    // (mirrored server-side in extract-roster) so an iPhone user never
    // pays for a doomed upload + Vision call.
    const name = (file.name || '').toLowerCase()
    const isHeic =
      /\.(heic|heif)$/.test(name) ||
      file.type === 'image/heic' ||
      file.type === 'image/heif'
    if (isHeic) {
      setError('Please use a JPG or PNG photo.')
      return
    }
    // Accept only jpg/png/webp images. Anything else (PDF, other docs) is
    // rejected before any upload or model call.
    const isAcceptedImage =
      ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      /\.(jpg|jpeg|png|webp)$/.test(name)
    if (!file.type?.startsWith('image/') || !isAcceptedImage) {
      setError('Take a photo or upload a JPG or PNG.')
      return
    }
    if (file.size === 0) {
      setError('That file looks empty — try taking the photo again.')
      return
    }
    if (file.size > 12 * 1024 * 1024) {
      setError('That photo is over 12 MB — try a smaller one.')
      return
    }
    setFilename(file.name)
    setError(null)

    // Block while the unit context is still loading. Before load,
    // isGrouped can transiently read false and the resolver would guess
    // the first active unit — the mis-attribution bug. No upload.
    if (loading) {
      setError('One moment — still loading your units.')
      setPhase('pick')
      return
    }

    // Resolve the destination unit BEFORE uploading. Grouped operator on
    // the all-units roll-up resolves to null — do NOT guess; block and
    // prompt them to pick a unit.
    const restaurantId = await resolveActiveRestaurantId({ currentRestaurantId, isGrouped })
    if (!restaurantId) {
      setError(
        isGrouped
          ? 'Select a unit first.'
          : 'We could not find your restaurant. Try reopening this screen.'
      )
      setPhase('pick')
      return
    }
    setResolvedRestaurantId(restaurantId)

    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoPreview(URL.createObjectURL(file))

    setPhase('uploading')
    let path
    try {
      path = await uploadReceivePhoto(file, restaurantId)
      setPhotoPath(path)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RosterScanModal] upload failed', err)
      setError('Could not upload the photo. Try again.')
      setPhase('error')
      return
    }

    setPhase('extracting')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sessData } = await supabase.auth.getSession()
      const token = sessData?.session?.access_token || anonKey

      const fnUrl = `${supabaseUrl}/functions/v1/extract-roster`
      const resp = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'authorization': `Bearer ${token}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({ photo_path: path, restaurant_id: restaurantId, kind }),
      })

      if (!resp.ok && resp.status >= 500) {
        throw new Error(`extract-roster HTTP ${resp.status}`)
      }
      const data = await resp.json().catch(() => null)
      if (!data) throw new Error('Empty response from extract-roster')

      // Deterministic fallbacks — seed NOTHING.
      if (data.error === 'image_unreadable') {
        setFallbackMsg("We couldn't read this — try a clearer photo.")
        setPhase('fallback')
        return
      }
      if (data.error === 'unsupported_type') {
        setFallbackMsg(data.message || 'Take a photo or upload a JPG or PNG.')
        setPhase('fallback')
        return
      }
      if (data.error) {
        setError(data.message || 'Extraction failed. Try again.')
        setPhase('error')
        return
      }
      if (data.empty || data.confidence === 'low' ||
          ((data.staff?.length ?? 0) === 0 && (data.shifts?.length ?? 0) === 0)) {
        setFallbackMsg("We couldn't read this — try a clearer photo.")
        setPhase('fallback')
        return
      }

      hydrate(data)
      setPhase('review')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RosterScanModal] extraction failed', err)
      setError("Couldn't process this photo. Try a clearer one or check your connection.")
      setPhase('error')
    }
  }

  function hydrate(data) {
    // Staff rows: pre-match each extracted name; default action is
    // 'match' if it lines up with existing staff, else 'create'.
    const sRows = (data.staff ?? []).map((s, idx) => {
      const name = (s.name || '').trim()
      const hit = matchStaffName(name, activeStaff)
      return {
        id:          `s${idx}`,
        name,
        role:        (s.role || '').trim(),
        action:      hit ? 'match' : 'create',
        matchedId:   hit?.staff.id ?? null,
        matchedName: hit?.staff.name ?? null,
      }
    }).filter((r) => r.name)

    // Shift rows: match staff_name against existing ∪ to-be-created.
    const fRows = (data.shifts ?? []).map((sh, idx) => {
      const staffName = (sh.staff_name || '').trim()
      const m = resolveShiftMatch(staffName, sRows)
      return {
        id:          `f${idx}`,
        day:         (sh.day || '').trim() || null,
        shift_date:  normDate(sh.shift_date),
        start_time:  normTime(sh.start_time),
        end_time:    normTime(sh.end_time),
        role:        (sh.role || '').trim(),
        staff_name:  staffName,
        matchedId:   m.matchedId,
        matchedName: m.matchedName,
        approved:    m.matchedId !== null,
      }
    })

    setStaffRows(sRows)
    setShiftRows(fRows)
  }

  function updateStaff(id, patch) {
    setStaffRows((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
      // Re-resolve shift matches against the updated staff set so a
      // toggled create/skip immediately reflects in the shift badges.
      setShiftRows((sPrev) =>
        sPrev.map((sh) => {
          const m = resolveShiftMatch(sh.staff_name, next)
          return {
            ...sh,
            matchedId: m.matchedId,
            matchedName: m.matchedName,
            approved: m.matchedId === null ? false : sh.approved,
          }
        })
      )
      return next
    })
  }

  function removeStaff(id) {
    setStaffRows((prev) => {
      const next = prev.filter((r) => r.id !== id)
      setShiftRows((sPrev) =>
        sPrev.map((sh) => {
          const m = resolveShiftMatch(sh.staff_name, next)
          return {
            ...sh,
            matchedId: m.matchedId,
            matchedName: m.matchedName,
            approved: m.matchedId === null ? false : sh.approved,
          }
        })
      )
      return next
    })
  }

  function updateShift(id, patch) {
    setShiftRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r
        const merged = { ...r, ...patch }
        if ('staff_name' in patch) {
          const m = resolveShiftMatch(patch.staff_name, staffRows)
          merged.matchedId = m.matchedId
          merged.matchedName = m.matchedName
          if (m.matchedId === null) merged.approved = false
        }
        return merged
      })
    )
  }

  function removeShift(id) {
    setShiftRows((prev) => prev.filter((r) => r.id !== id))
  }

  async function handleSubmit() {
    if (phase === 'submitting') return

    // Reuse the SAME unit resolved at photo-pick time — do NOT re-resolve.
    const restaurantId = resolvedRestaurantId
    if (!restaurantId) {
      setError(
        isGrouped ? 'Select a unit first.' : 'We could not find your restaurant. Start the scan again.'
      )
      return
    }

    const createRows = staffRows.filter((s) => s.action === 'create' && s.name.trim())
    const approvableShifts = shiftRows.filter((s) => s.approved)
    if (createRows.length === 0 && approvableShifts.length === 0) {
      setError('Nothing to import — add or approve at least one row.')
      return
    }

    setPhase('submitting')
    setError(null)
    let succeeded = false

    try {
      // ---------- Step A: insert NEW staff ----------
      // Dedupe by normalized name vs existing active staff AND within the
      // batch. EXPLICIT restaurant_id on every row.
      const existingNorms = new Set(
        (activeStaff ?? []).map((s) => normalizeStaffName(s.name))
      )
      const seen = new Set()
      const staffInserts = []
      const pendingByNorm = new Map()  // norm name → staff-row id (for later wiring)
      for (const r of createRows) {
        const norm = normalizeStaffName(r.name)
        if (!norm || existingNorms.has(norm) || seen.has(norm)) continue
        seen.add(norm)
        pendingByNorm.set(norm, r.id)
        staffInserts.push({
          restaurant_id: restaurantId,
          name:          r.name.trim(),
          role:          r.role?.trim() || null,
          hourly_wage:   null,
          email:         null,
          phone:         null,
          notes:         'Imported from photo',
          active:        true,
        })
      }

      let createdStaff = []
      if (staffInserts.length > 0) {
        const { data, error: staffErr } = await supabase
          .from('staff')
          .insert(staffInserts)
          .select('id, name')
        if (staffErr) throw staffErr
        createdStaff = data ?? []
      }

      // Build a normalized-name → staff_id map across existing ∪ created.
      const staffIdByNorm = new Map()
      for (const s of activeStaff ?? []) {
        staffIdByNorm.set(normalizeStaffName(s.name), s.id)
      }
      for (const s of createdStaff) {
        staffIdByNorm.set(normalizeStaffName(s.name), s.id)
      }

      // ---------- Step B: map each approved shift to a staff_id ----------
      // Use the matcher against the COMBINED set; skip unmatched. A shift
      // is only WRITABLE with BOTH a start AND an end time (end_time is
      // NOT NULL in schedule_assignments) PLUS a date and a matched staff.
      // Rows missing an end are EXCLUDED from the insert batch — never
      // fabricated — and counted separately so the UI can nudge.
      const datedShifts = []
      let skippedUnmatched = 0
      let skippedNoEnd = 0
      for (const sh of approvableShifts) {
        if (!sh.shift_date || !sh.start_time) { skippedUnmatched++; continue }
        if (!sh.end_time) { skippedNoEnd++; continue }
        // Prefer the existing-staff match; otherwise the just-created one.
        let staffId = null
        const existing = matchStaffName(sh.staff_name, activeStaff)
        if (existing) staffId = existing.staff.id
        else staffId = staffIdByNorm.get(normalizeStaffName(sh.staff_name)) ?? null
        if (!staffId) { skippedUnmatched++; continue }
        datedShifts.push({ ...sh, _staffId: staffId })
      }

      // ---------- Step C: insert schedule_assignments ----------
      // Dedupe vs existing rows in the date range AND within the batch.
      let skippedDupe = 0
      let shiftsInserted = 0
      if (datedShifts.length > 0) {
        const dates = datedShifts.map((s) => s.shift_date)
        // Defensive initial values — datedShifts is non-empty here, but a
        // reduce over an empty array throws without a seed.
        const minDate = dates.reduce((a, b) => (a < b ? a : b), dates[0])
        const maxDate = dates.reduce((a, b) => (a > b ? a : b), dates[0])
        const { data: existing, error: existErr } = await supabase
          .from('schedule_assignments')
          .select('staff_id, shift_date, start_time')
          // Only this unit's existing rows — avoids false-dupe skips
          // against a multi-unit member's OTHER units' assignments.
          .eq('restaurant_id', restaurantId)
          .gte('shift_date', minDate)
          .lte('shift_date', maxDate)
        if (existErr) throw existErr
        const existingKeys = new Set(
          (existing ?? []).map((e) =>
            assignmentDedupeKey(e.staff_id, e.shift_date, e.start_time)
          )
        )

        const batchKeys = new Set()
        const inserts = []
        for (const s of datedShifts) {
          const key = assignmentDedupeKey(s._staffId, s.shift_date, s.start_time)
          if (existingKeys.has(key) || batchKeys.has(key)) { skippedDupe++; continue }
          batchKeys.add(key)
          inserts.push({
            // EXPLICIT restaurant_id (same unit as the photo).
            restaurant_id: restaurantId,
            staff_id:      s._staffId,
            shift_date:    s.shift_date,
            start_time:    s.start_time,
            // end_time is guaranteed present (Step B excluded null-end
            // rows) — schedule_assignments.end_time is NOT NULL.
            end_time:      s.end_time,
            role:          s.role || null,
            status:        'scheduled',
          })
        }
        if (inserts.length > 0) {
          const { error: insErr } = await supabase
            .from('schedule_assignments')
            .insert(inserts)
          if (insErr) throw insErr
          shiftsInserted = inserts.length
        }
      }

      succeeded = true
      onImported?.({
        staffCreated:     createdStaff.length,
        shiftsInserted,
        skippedDupe,
        skippedUnmatched,
        skippedNoEnd,
      })
      onClose?.()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RosterScanModal] submit failed', err)
      const isRls = err?.code === '42501' || /row-level security/i.test(err?.message || '')
      setError(
        isRls
          ? "This couldn't be saved to the selected unit — check that you have access to it."
          : "Couldn't save this import. Try again."
      )
    } finally {
      // Submit-state reset in finally (NOT catch). On success the modal
      // closes; on failure we drop back to the editable review so the
      // operator can retry — the form never locks on the next open.
      if (!succeeded) setPhase('review')
    }
  }

  // ---------- Render ---------------------------------------
  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Scan schedule photo"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Scan schedule photo</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* ---------- Phase: PICK ---------- */}
          {phase === 'pick' && (
            <>
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
                Snap a clear photo of a printed schedule or staff list. We
                pull out the staff and shifts so you don't have to type them.
                You'll review and edit everything before it's saved.
              </div>

              <input
                ref={fileRef}
                id="roster-photo"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={handleFile}
                className="sr-only"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-lg font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
              >
                Take photo of schedule
              </button>
              <p className="text-center text-xs text-black/55">
                Opens your camera. Tips: lay the schedule flat, fill the
                frame, avoid glare, hold steady. JPG or PNG only.
              </p>
            </>
          )}

          {/* ---------- Phase: UPLOAD / EXTRACT (loading) ---------- */}
          {(phase === 'uploading' || phase === 'extracting') && (
            <div className="flex flex-col items-center gap-4 py-8">
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="Schedule"
                  className="max-h-48 rounded-lg border border-black/10 bg-black/5 object-contain"
                />
              )}
              {phase === 'uploading' ? (
                <KitchenLoader
                  align="row"
                  size="sm"
                  messages={['Uploading photo…']}
                />
              ) : (
                <KitchenLoader
                  messages={[
                    'Reading the schedule…',
                    'Picking out names…',
                    'Matching to your staff…',
                    'Lining up the shifts…',
                  ]}
                />
              )}
            </div>
          )}

          {/* ---------- Phase: FALLBACK (couldn't read) ---------- */}
          {phase === 'fallback' && (
            <div className="space-y-3">
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900">
                {fallbackMsg || "We couldn't read this — try a clearer photo."}
              </div>
              <button
                type="button"
                onClick={() => setPhase('pick')}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Try another photo
              </button>
            </div>
          )}

          {/* ---------- Phase: REVIEW ---------- */}
          {phase === 'review' && (
            <>
              {photoPreview && (
                <a
                  href={photoPreview}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  title="View full-size photo"
                >
                  <img
                    src={photoPreview}
                    alt="Schedule"
                    className="block max-h-40 w-full rounded-lg border border-black/10 bg-black/5 object-contain"
                  />
                </a>
              )}

              {/* Staff section */}
              {(kind === 'staff' || kind === 'both') && (
                <section>
                  <h3 className="mb-2 text-sm font-medium text-black/80">
                    Staff ({staffRows.length})
                  </h3>
                  <div className="space-y-2">
                    {staffRows.map((row) => (
                      <StaffRowEditor
                        key={row.id}
                        row={row}
                        onChange={(patch) => updateStaff(row.id, patch)}
                        onRemove={() => removeStaff(row.id)}
                      />
                    ))}
                    {staffRows.length === 0 && (
                      <div className="rounded-xl border border-dashed border-black/15 px-4 py-4 text-center text-sm text-black/60">
                        No staff found in this photo.
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Shifts section */}
              {(kind === 'schedule' || kind === 'both') && (
                <section>
                  <h3 className="mb-2 text-sm font-medium text-black/80">
                    Shifts ({shiftRows.length})
                  </h3>
                  {shiftsUnmatched > 0 && (
                    <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2 text-xs text-amber-900">
                      <span className="font-medium tabular-nums">{shiftsUnmatched}</span>{' '}
                      {shiftsUnmatched === 1 ? 'shift has' : 'shifts have'} no staff match —
                      add the staff above or edit the name to import them.
                    </div>
                  )}
                  {shiftsMissingEnd > 0 && (
                    <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2 text-xs text-amber-900">
                      <span className="font-medium tabular-nums">{shiftsMissingEnd}</span>{' '}
                      {shiftsMissingEnd === 1 ? 'shift skipped' : 'shifts skipped'} — add an
                      end time to import {shiftsMissingEnd === 1 ? 'it' : 'them'}.
                    </div>
                  )}
                  <div className="space-y-2">
                    {shiftRows.map((row) => (
                      <ShiftRowEditor
                        key={row.id}
                        row={row}
                        onChange={(patch) => updateShift(row.id, patch)}
                        onRemove={() => removeShift(row.id)}
                      />
                    ))}
                    {shiftRows.length === 0 && (
                      <div className="rounded-xl border border-dashed border-black/15 px-4 py-4 text-center text-sm text-black/60">
                        No shifts found in this photo.
                      </div>
                    )}
                  </div>
                </section>
              )}

              <p className="text-xs text-black/55">
                Shifts with only a weekday (no date) and shifts with no
                matched staff are skipped. Re-importing is safe — duplicates
                are skipped automatically.
              </p>
            </>
          )}

          {/* ---------- Phase: SUBMITTING ---------- */}
          {phase === 'submitting' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <KitchenLoader align="row" size="sm" messages={['Saving roster…']} />
            </div>
          )}

          {/* ---------- Phase: ERROR (with retry) ---------- */}
          {phase === 'error' && (
            <div className="space-y-3">
              <p className="text-sm text-black/70">
                Something went wrong. Try a different photo or check your connection.
              </p>
              <button
                type="button"
                onClick={() => setPhase('pick')}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {phase === 'review' && (
          <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={staffToCreate === 0 && shiftsApproved === 0}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {staffToCreate === 0 && shiftsApproved === 0
                ? 'Nothing to import yet'
                : `Import ${importSummary(staffToCreate, shiftsApproved)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function importSummary(staffCount, shiftCount) {
  const parts = []
  if (staffCount > 0) parts.push(`${staffCount} ${staffCount === 1 ? 'staff' : 'staff'}`)
  if (shiftCount > 0) parts.push(`${shiftCount} ${shiftCount === 1 ? 'shift' : 'shifts'}`)
  return parts.join(' + ')
}

// ---------- Staff row editor ------------------------------
function StaffRowEditor({ row, onChange, onRemove }) {
  const isMatch = row.action === 'match'
  return (
    <div className={`rounded-xl border p-3 ${
      isMatch ? 'border-black/15 bg-white' : 'border-emerald-300 bg-emerald-50/40'
    }`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        {isMatch ? (
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
            Already in staff: {row.matchedName}
          </span>
        ) : (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
            New staff
          </span>
        )}
        <div className="flex items-center gap-2">
          <select
            value={row.action}
            onChange={(e) => onChange({ action: e.target.value })}
            className="rounded-lg border border-black/15 bg-white px-2 py-1 text-xs outline-none focus:border-blue-600"
          >
            <option value="create">Add as new</option>
            {row.matchedId && <option value="match">Use existing match</option>}
            <option value="skip">Skip</option>
          </select>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove this staff row"
            className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-black/40 hover:bg-red-50 hover:text-red-600"
          >
            {'✕'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={row.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Staff name"
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
        <input
          type="text"
          value={row.role}
          onChange={(e) => onChange({ role: e.target.value })}
          placeholder="Role (optional)"
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
      </div>
    </div>
  )
}

// ---------- Shift row editor ------------------------------
function ShiftRowEditor({ row, onChange, onRemove }) {
  const matched = row.matchedId !== null
  // Matched + flagged for import but missing the (NOT NULL) end time:
  // the row is excluded from the insert batch until an end is filled in.
  const needsEnd = matched && row.approved && !!row.start_time && !row.end_time
  return (
    <div className={`rounded-xl border p-3 ${
      matched ? 'border-black/15 bg-white' : 'border-amber-300 bg-amber-50/40'
    }`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        {matched ? (
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
            {row.matchedName}
          </span>
        ) : (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            No staff match
          </span>
        )}
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium">
            <input
              type="checkbox"
              checked={row.approved}
              disabled={!matched}
              onChange={(e) => onChange({ approved: e.target.checked })}
              className="h-4 w-4"
            />
            Import
          </label>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove this shift row"
            className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-black/40 hover:bg-red-50 hover:text-red-600"
          >
            {'✕'}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <input
          type="text"
          value={row.staff_name}
          onChange={(e) => onChange({ staff_name: e.target.value })}
          placeholder="Staff name on this shift"
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-medium text-black/55">Date</label>
            <input
              type="date"
              value={row.shift_date || ''}
              onChange={(e) => onChange({ shift_date: e.target.value })}
              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base outline-none focus:border-blue-600"
            />
            {!row.shift_date && row.day && (
              <p className="mt-0.5 text-[10px] text-amber-700">
                Only {row.day} shown — set a date to import.
              </p>
            )}
          </div>
          <div>
            <label className="text-[10px] font-medium text-black/55">Role</label>
            <input
              type="text"
              value={row.role}
              onChange={(e) => onChange({ role: e.target.value })}
              placeholder="optional"
              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base outline-none focus:border-blue-600"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-medium text-black/55">Start</label>
            <input
              type="time"
              value={row.start_time || ''}
              onChange={(e) => onChange({ start_time: e.target.value })}
              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base outline-none focus:border-blue-600"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-black/55">End</label>
            <input
              type="time"
              value={row.end_time || ''}
              onChange={(e) => onChange({ end_time: e.target.value })}
              className={`w-full rounded-lg border px-2 py-2 text-base outline-none focus:border-blue-600 ${
                needsEnd ? 'border-amber-400 bg-amber-50/40' : 'border-black/15'
              }`}
            />
            {needsEnd && (
              <p className="mt-0.5 text-[10px] text-amber-700">
                Add an end time to import.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------- helpers ----------------------------------------
// Normalize a model-supplied date to YYYY-MM-DD or null.
function normDate(v) {
  if (typeof v !== 'string') return null
  const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? m[0] : null
}
// Normalize a model-supplied time to HH:MM or null.
function normTime(v) {
  if (typeof v !== 'string') return null
  const m = v.trim().match(/^(\d{1,2}):(\d{2})/)
  if (!m) return null
  const h = String(Math.min(23, Number(m[1]))).padStart(2, '0')
  return `${h}:${m[2]}`
}
